// "Pick one of this site's files" — the modal, and the two ways of opening it.
//
// The archived project had this as `@repoflow/components/file-selector` and
// three things opened it: the paragraph editor's toolbar, the `image` widget
// and the `url` widget. All three are back, and so is this — but it lives in
// the app rather than in @repoflow/components, and the reason is the one
// ../../widgets/paragraph gives for staying here.
//
// It cannot be a shared component. Its whole body is `queries/site-file`, this
// service's schema and this service's URL builder; the archived one could be
// shared because it was handed a `domain`, a `bucket` and an `extract` function
// through a React context a Provider high in the tree filled in
// (`site-frontend/src/components/file-provider`). That indirection bought
// genericity nothing ever used — one app, one file service — and cost a context
// that had to be mounted before any widget deep in a form could work, with no
// error when it was not: `configSelector` came back null and the Select button
// silently did not render. Reading the site off the section context instead
// means the button is there or the screen is broken.
//
// WHAT IT HANDS BACK, AND THE ONE THING IT WRITES
//
// The PUBLIC path — `/file/<kind>/<fileId>/content/<name>.<ext>` — which is
// what a visitor to the published site resolves on that site's own verified
// hostname, and which carries no hostname itself so that attaching, detaching
// or swapping a domain changes nothing that was written into a page
// (docs/pagews-files.md §7).
//
// That path answers only for a file whose `visibility` is `public`, and a NEW
// image already is: `defaultVisibilityOf` makes images public and documents
// private at create, because the two failure modes are not symmetric — an image
// defaulting closed would work in the editor and break on the published page,
// diagnosed by nobody, since the byte route answers a miss and a refusal
// identically. So the ordinary row here offers a plain "Select".
//
// Two kinds of row do not. One written before the field existed is private by
// ABSENCE whatever its kind (`visibility.js` says why), and one can be closed
// deliberately on the file's own screen. For those this modal is the only place
// in the app that WRITES that field from outside that screen, and it says so on
// the button — "Publish and use" rather than "Select".
//
// That is not a way around the flag, it is the flag being used. `visibility.js`
// puts it as "`private` is not hidden from the owner — it is not part of the
// website", and choosing a picture for a page is exactly the person saying this
// one is. What the field protects against is a row becoming public by a DEPLOY
// rather than by a person, and nothing here changes that: every publish is one
// click by one person on one file, on a button that says what it does.
import React, { useMemo, useRef, useState } from "react";
import { Alert, Button } from "reactstrap";
import { Link } from "react-router-dom";
import { useMutation } from "@apollo/client";
import * as ModalComp from "@repoflow/form-schema/modal";
import * as Notify from "@repoflow/form-schema/notify";
import { List } from "@repoflow/components/list-cursor";
import { useSectionContext } from "@repoflow/components/section";
import * as SiteFileQL from "queries/site-file";
import SiteFileSchema from "@common/schema/site-file/public";
import {
  IMAGES,
  KIND_CONFIG,
  isPreviewExt,
} from "@common/schema/site-file/kind";
import { isPublic, PUBLIC } from "@common/schema/site-file/visibility";
import { siteFilePublicContentUrl } from "@common/schema/site-file/url";
import { TagFilter, useTagFilter } from "../tag-filter";

export { useModalState } from "@repoflow/form-schema/modal";

// The columns. Fewer than the Files listing shows, because this is a chooser
// and not an inventory: `status` earns its place for the same reason it does
// there — a row created seconds ago has no bytes — and `visibility` because it
// is what the button on the row is about to say.
const FIELDS = {
  "/Public/SiteFile": {
    fields: ["name", "ext", "size", "status", "visibility"],
  },
};

// WHETHER A ROW'S BYTES COULD BE SHOWN ON A PAGE AT ALL.
//
// Deliberately NOT a question about visibility — that one is a decision the
// person can still make, and ../file-selector offers it on the row. These two
// are facts about the file that no click can change.
//
// `size` and not `status`, the same gate the details screen uses and for the
// same reason: bucketws writes the object and the real size in one step and
// only then leaves an image at "processing" while it generates minimaps, so a
// row can be perfectly readable and still say processing. A row with no bytes
// yet carries the `size: 0` the create wrote.
//
// `isPreviewExt` is the second and it is not about previewing. bucketws stores
// every "active" content type — svg among them — as
// `application/octet-stream` with `Content-Disposition: attachment`, so an
// `<img src>` pointed at one downloads instead of rendering. Offering it here
// would put a broken image on a page.
export const showableOf = (file) => {
  const { fileId, ext, size } = file || {};

  return Boolean(fileId && ext && Number(size) > 0 && isPreviewExt(ext));
};

// The path a page carries for a row, or null if it could never be one.
//
// Visibility is not consulted here either: the URL a public file resolves at is
// the URL it will resolve at once it IS public, and building it before the
// write is what lets the publish and the insert be one action.
export const publicPathOf = (file) => {
  if (!showableOf(file)) {
    return null;
  }

  const { kind, fileId, name, ext } = file;

  return siteFilePublicContentUrl({ kind, fileId, name, ext });
};

// THE UPDATE THAT PUBLISHES A FILE, AND WHY IT SENDS TWO FIELDS IT IS NOT
// CHANGING.
//
// `SiteFileInputUpdate` declares `description: String!` and `tags: [String]!`.
// Only `visibility` is nullable, deliberately — so a client that omits it is not
// read as closing a file it never meant to touch. The model treats an omitted
// field as unchanged for all three, but GraphQL validation refuses the call
// before the model can say so, so the other two cannot simply be left out.
//
// Which means this update REPLACES them, and a lazy `{ description: "", tags: []
// }` would erase the caption and the tags somebody typed as a side effect of
// putting the picture on a page. They are echoed back off the row instead. The
// row has them: `SiteFileFragment` selects both, and the listing this modal
// renders uses that fragment — the `config` above only chooses which COLUMNS
// are shown, not what is fetched.
//
// Its own function, and exported, because the failure is silent and after the
// fact: the mutation succeeds, the image appears on the page, and the
// description is gone with nothing on screen having mentioned it.
export const publishInputOf = (file) => ({
  description: (file && file.description) || "",
  tags: (file && file.tags) || [],
  visibility: PUBLIC,
});

// One row's button, which is three different buttons depending on the row.
const Row = ({ item, siteId, onSelect, close }) => {
  const [publish, { loading }] = useMutation(SiteFileQL.edit, {
    update: SiteFileQL.update(),
  });

  const path = publicPathOf(item);

  if (!path) {
    // Not a disabled button with no explanation. The two reasons a row's bytes
    // can never go on a page are entirely different — one resolves itself in
    // seconds and the other never will — and somebody looking at their own
    // freshly uploaded file needs to be told which.
    return (
      <span className="text-muted">
        {Number(item.size) > 0
          ? "Not an image a page can show"
          : "Still uploading"}
      </span>
    );
  }

  if (isPublic(item)) {
    return (
      <Button
        type="button"
        color="primary"
        onClick={() => {
          onSelect(path, item);
          close();
        }}
      >
        Select
      </Button>
    );
  }

  return (
    <Button
      type="button"
      color="warning"
      disabled={loading}
      // Says what it does. A "Select" here that quietly flipped the file to
      // public would be the one thing `visibility` exists to prevent — a file
      // becoming part of the website without anyone deciding it. Rare, because
      // a new image is public already; the rows that reach this branch are the
      // ones that predate the field and the ones somebody closed on purpose.
      title="This file is private. Using it on a page publishes it."
      onClick={async () => {
        try {
          await publish({
            variables: {
              siteId,
              kind: item.kind,
              fileId: item.fileId,
              input: publishInputOf(item),
            },
          });
        } catch (e) {
          // The insert does NOT happen. A path written into a paragraph for a
          // file that is still private is an image that renders on no page,
          // and the person would have no way to tell that from a picture that
          // simply has not deployed yet.
          Notify.error(`${item.name} could not be published: ${e.toString()}`);
          return;
        }

        onSelect(path, item);
        close();
      }}
    >
      {loading ? "Publishing…" : "Publish and use"}
    </Button>
  );
};

// The modal's body. Mounted only while the modal is open — reactstrap's Modal
// renders nothing when it is not — which keeps the listing query off every
// screen that merely has a picker on it.
const Body = ({ close, context: { kind, onSelect, siteId } }) => {
  // WHY THE ROW COMPONENT IS MEMOISED AND ITS TWO CALLBACKS GO THROUGH REFS
  //
  // `content` is rendered by list-cursor as `<Content item={item} />` — as a
  // component TYPE. An arrow written inline here is a new value on every render
  // of this body, so React sees a type it has not met before and REMOUNTS every
  // row. That is the same mechanism P139 spent weeks on, one level down.
  //
  // This body re-renders more often than it looks: it is the modal's children,
  // and the modal is rendered by the widget that opened it — so every keystroke
  // in the `url` widget's text box, behind the modal, re-renders this. A remount
  // mid-flight resets the publish mutation's `loading`, so the button would say
  // "Publish and use" again while the write it started was still in the air.
  //
  // Memoising on `siteId` alone is what keeps the type stable, and the two
  // callbacks cannot be dependencies: `close` is rebuilt by useModalState on
  // every render and `onSelect` by the widget above, so a dependency on either
  // would invalidate the memo on exactly the renders it exists to survive.
  // Read through refs instead, which is the same shape the paragraph editor
  // uses for `setValue`.
  const handlers = useRef({ onSelect, close });
  handlers.current = { onSelect, close };

  const Content = useMemo(
    () =>
      ({ item }) => (
        <Row
          item={item}
          siteId={siteId}
          onSelect={(...args) => handlers.current.onSelect(...args)}
          close={() => handlers.current.close()}
        />
      ),
    [siteId]
  );

  // WHICH of this site's images, which is the question a chooser opened at a
  // site with forty of them cannot otherwise answer. `kind` is the only thing
  // narrowing this list today, and it narrows it to "all the images".
  //
  // The tags are the rows' own — ../tag-filter builds the choices out of what
  // came back, so this needs nothing from the query it did not already select
  // — and a locale code is the case it was added for: the Spanish hero image
  // and the English one are two uploads with two ids, and a tag is what tells
  // them apart (P206).
  const tagFilter = useTagFilter();

  return (
    <>
      <TagFilter state={tagFilter} />
      <List
        path={"viewer.account.sites.get.files"}
        query={SiteFileQL.list}
        variables={{ siteId, kind }}
        schema={SiteFileSchema}
        target={{ $ref: "/Public/SiteFile" }}
        config={FIELDS}
        // A file uploaded in another tab a moment ago should be here. The listing
        // screen can afford the cache; a chooser opened to find one specific file
        // cannot, and "it is not in the list" is indistinguishable from "the upload
        // failed".
        fetchPolicy="cache-and-network"
        onData={tagFilter.onData}
        filter={tagFilter.filter}
        empty={
          tagFilter.active ? (
            // Which empty this is. "No images on this site yet" under an
            // active filter would send somebody to upload a file they already
            // have.
            <>
              No {KIND_CONFIG[kind].label.toLowerCase()} tagged{" "}
              <b>{tagFilter.tag}</b> among the ones loaded.
            </>
          ) : (
            <>
              No {KIND_CONFIG[kind].label.toLowerCase()} on this site yet.{" "}
              {/* The way out, because a modal cannot upload one: the upload screen is
                three steps with a presigned POST in the middle, and a copy of it in
                here would be a second implementation of the one thing in this
                service that has already shipped broken once. The link leaves the
                form, which is why it is only offered when there is nothing to
                choose. */}
              <Link to={`/site/${siteId}/file/${kind}`}>Upload one</Link>.
            </>
          )
        }
        content={Content}
      />
    </>
  );
};

// The modal itself, driven by a state object the caller owns.
//
// The caller owns it because one of the three openers is not a React event at
// all: Quill's toolbar calls a plain function from outside the component tree,
// so the paragraph widget has to open this without a button of its own.
// `SelectButton` below is the convenience wrapper for the two that do.
export const Component = ({ state, kind = IMAGES, onSelect }) => {
  const context = useSectionContext();
  const siteId =
    context && context.attributes ? context.attributes.siteId : null;

  // The site route puts `siteId` in `attributes`, so a picker mounted outside a
  // site is a bug in whoever mounted it rather than a state to render around.
  // It says so instead of rendering an empty list, which is what the archived
  // project's silently-absent Provider did.
  if (!siteId) {
    return (
      <ModalComp.Component state={state} title="Select a file">
        {() => (
          <Alert color="danger">
            This picker is not inside a site, so it has nothing to list.
          </Alert>
        )}
      </ModalComp.Component>
    );
  }

  return (
    <ModalComp.Component
      state={state}
      title={`Select from ${KIND_CONFIG[kind].label}`}
      context={{ kind, onSelect, siteId }}
    >
      {Body}
    </ModalComp.Component>
  );
};

// A Select button with the modal behind it, for the callers that open it from a
// click. `type="button"` because these sit inside forms, where the default is
// submit.
export const SelectButton = ({
  kind = IMAGES,
  onSelect,
  children = "Select",
  ...rest
}) => {
  const state = ModalComp.useModalState(false);

  return (
    <>
      <Button
        type="button"
        color="primary"
        onClick={(e) => {
          e.preventDefault();
          state.open();
        }}
        {...rest}
      >
        {children}
      </Button>
      <Component state={state} kind={kind} onSelect={onSelect} />
    </>
  );
};

// What a widget shows for the value it is holding.
//
// NOT the public path, which is the thing being stored: that one resolves on
// the site's own verified hostname and answers nothing on the app's, which is
// the origin this screen is on (P142 — the app deliberately serves no tenant
// content). So the preview is built from the AUTHENTICATED path, which is what
// the person looking at it has a session for.
//
// Given only a stored string there is no row to build that from, so a value
// this cannot map back — a URL somebody typed, one from another site — simply
// shows nothing. A widget that guessed would be showing the wrong picture.
export const Preview = ({
  url,
  className = "img-fluid border rounded",
  alt = "",
}) => {
  const [broken, setBroken] = useState(false);
  const context = useSectionContext();
  const siteId =
    context && context.attributes ? context.attributes.siteId : null;

  const src = previewSrcOf(url, siteId);

  if (!src || broken) {
    return null;
  }

  return (
    <img
      className={className}
      style={{ maxHeight: "12rem" }}
      src={src}
      alt={alt}
      // A URL that was valid when it was picked and is not now — the file was
      // removed. Rendering nothing beats a browser's broken-image glyph, which
      // reads as a bug in the editor.
      onError={() => setBroken(true)}
    />
  );
};

// The public path a page carries, back to the authenticated one this origin
// answers — `/file/<kind>/<fileId>/content/<name>` to
// `/file/<siteId>/<kind>/<fileId>/content/<name>`.
//
// Exported because it is the only part of the Preview worth pinning, and it is
// a string transform rather than a component.
//
// Anything that is not exactly that shape comes back null: an absolute URL, a
// relative path to something else, an empty field. Those are all legitimate
// values for the fields this widget is on — a favicon on a CDN is an ordinary
// answer — they simply are not this site's files, and the preview has nothing
// to say about them.
export const previewSrcOf = (url, siteId) => {
  if (typeof url !== "string" || !siteId) {
    return null;
  }

  const match = /^\/file\/([^/]+)\/([^/]+)\/content\/([^/]+)$/.exec(url);
  if (!match) {
    return null;
  }

  const [, kind, fileId, filename] = match;

  return `/file/${encodeURIComponent(siteId)}/${kind}/${fileId}/content/${filename}`;
};

// The inverse, and the reason there is one.
//
// `previewSrcOf` is not only for a widget looking at its own value: the
// paragraph editor shows the picture INSIDE Quill's document, and that document
// is on this origin too. So the delta is mapped to display URLs on its way into
// the editor and mapped back on its way out — see ../../widgets/paragraph/urls.
//
// Which makes this pair a round trip rather than two conveniences, and a round
// trip that has to be exact: what comes back out of the editor is what gets
// STORED, so a mapping that did not reverse cleanly would write the app's own
// authenticated path into a page and answer 401 for every visitor.
export const storedSrcOf = (url, siteId) => {
  if (typeof url !== "string" || !siteId) {
    return null;
  }

  const match = /^\/file\/([^/]+)\/([^/]+)\/([^/]+)\/content\/([^/]+)$/.exec(url);
  if (!match) {
    return null;
  }

  const [, site, kind, fileId, filename] = match;

  // Another site's authenticated path is not this site's to rewrite. It cannot
  // arrive from the picker — which only lists this site — but a delta can be
  // copied between pages, and silently re-pointing it at this site would be a
  // worse answer than leaving it alone.
  if (site !== encodeURIComponent(siteId)) {
    return null;
  }

  return `/file/${kind}/${fileId}/content/${filename}`;
};
